/**
 * Migas de pan.
 *
 * La ruta no se declara en el frontend: se deduce del arbol de menus, que es lo
 * que el backend ya sabe. Asi, un modulo que reordena sus menus reordena tambien
 * las migas sin tocar codigo de UI.
 *
 * En pantallas chicas se muestran solo los dos ultimos tramos: el resto ocuparia
 * mas que el titulo.
 */

import { Link } from 'react-router-dom';

import { IconChevronRight } from '@/components/shell/icons';
import { findMenuPath, findMenuPathByName, menuPath, modelPath } from '@/components/shell/menu-utils';
import { useMenusQuery, useMetaQuery } from '@/components/shell/queries';

interface Crumb {
  label: string;
  to?: string;
}

export function Breadcrumbs({
  currentModel,
  /** Menu por el que se entro: decide cual de las ramas posibles se muestra. */
  currentMenu,
  /** Ultimo tramo, cuando se esta viendo un registro concreto. */
  trailing,
}: {
  currentModel: string | null;
  currentMenu?: string | null;
  trailing?: string;
}) {
  const { data: menus } = useMenusQuery();
  const { data: meta } = useMetaQuery(currentModel);

  const crumbs: Crumb[] = [{ label: 'Inicio', to: '/' }];

  if (currentModel !== null) {
    // Con `?menu=` la rama es exacta; sin el hay que adivinar por modelo, y ahi
    // «Clientes» aparece como «Contactos ▸ Todos». Es el precio de una URL
    // escrita a mano, no un error de la navegacion normal.
    const ruta =
      menus === undefined
        ? []
        : currentMenu === undefined || currentMenu === null || currentMenu === ''
          ? findMenuPath(menus, currentModel)
          : findMenuPathByName(menus, currentMenu);

    if (ruta.length > 0) {
      ruta.forEach((node, indice) => {
        const esUltimo = indice === ruta.length - 1;
        const destino = menuPath(node);
        // Las ramas no son navegables: agrupan, no abren una pantalla.
        if (destino !== null && esUltimo) {
          crumbs.push({ label: node.label, to: destino });
        } else {
          crumbs.push({ label: node.label });
        }
      });
    } else if (meta !== undefined) {
      // Modelo alcanzado por URL directa, sin entrada de menu. Se dibuja con el
      // nombre HUMANO que declara el ModelSpec, nunca con el tecnico.
      crumbs.push({ label: meta.label_plural, to: modelPath(currentModel) });
    }
    // Si la metadata todavia no llego —o no va a llegar nunca porque el usuario
    // no tiene permiso (403) o el modelo no existe (404)— el tramo se omite. El
    // reemplazo que habia (`?? currentModel`) escupia el nombre tecnico del
    // modelo en las migas, y donde mas se notaba era justo donde peor queda: al
    // entrar sin permiso, la pantalla decia «No tenes acceso a esta pantalla» y
    // arriba, en la ruta de navegacion, «accounting.journal_entry». Jerga en
    // ingles, con punto, contandole al usuario como se llama la tabla por
    // dentro. Preferimos una miga de menos: «Inicio > #32» no explica mucho,
    // pero no miente ni filtra el esquema.
  }

  if (trailing !== undefined) {
    crumbs.push({ label: trailing });
  }

  return (
    <nav aria-label="Ruta de navegación" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1 text-sm">
        {crumbs.map((crumb, indice) => {
          const esUltimo = indice === crumbs.length - 1;
          const esAnteultimo = indice === crumbs.length - 2;
          return (
            <li
              key={`${crumb.label}-${indice}`}
              className={[
                'flex min-w-0 items-center gap-1',
                esUltimo || esAnteultimo ? 'flex' : 'hidden sm:flex',
              ].join(' ')}
            >
              {indice > 0 && (
                <IconChevronRight
                  className={[
                    'h-3.5 w-3.5 shrink-0 text-fg-faint',
                    esAnteultimo ? 'hidden sm:block' : '',
                  ].join(' ')}
                />
              )}
              {crumb.to !== undefined && !esUltimo ? (
                <Link
                  to={crumb.to}
                  className="truncate rounded text-fg-muted transition-colors hover:text-fg hover:underline underline-offset-2"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="truncate font-medium text-fg" aria-current={esUltimo ? 'page' : undefined}>
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
